/**
 * Big Five Profile — shared trait & aspect metadata.
 *
 * Single source of truth for everything that should be the same on
 * BOTH the employee result page (`SelfServiceBigFiveResult`) and the
 * HR Admin individual-result modal (`IndividualResultModal`):
 *
 *   - Canonical trait order (O, C, E, A, N).
 *   - Trait display name, behavioural caption, low/high pole labels,
 *     and brand colour (used for the percentile bar and accent).
 *   - The 30 aspect (a.k.a. IPIP-NEO "facet") display names and
 *     one-sentence behavioural blurbs.
 *   - The qualitative bucket helper that maps a percentile (0..100)
 *     onto a 5-step Low / Below average / Average / Above average /
 *     High descriptor with a corresponding `<InfoBadge>` colour.
 *
 * The blurbs and bucket vocabulary were tuned for an employee reader
 * but they are also what HR sees in the individual-result modal — keeping
 * them consistent means the conversation HR has *with* the employee
 * uses the same language the employee saw on their own result page.
 *
 * Per the IPIP-NEO licence the trait/aspect names are public domain.
 */

export const TRAIT_ORDER = ['O', 'C', 'E', 'A', 'N'];

export const TRAIT_META = {
  O: {
    name: 'Openness',
    bar: 'bg-violet-600',
    dot: 'bg-violet-600',
    gradient: 'from-violet-50 to-fuchsia-50',
    caption: 'Curiosity, imagination, openness to experience and ideas.',
    low: 'Practical, conventional, prefers familiar routines.',
    high: 'Curious, imaginative, drawn to novelty and ideas.',
    // Raw hex twin of `bar`/`dot` (violet-600) — Recharts (SVG stroke/fill,
    // chart tooltips) cannot consume Tailwind class names, so charts need
    // the literal colour value. Keep this in lock-step with `bar`/`dot`.
    hex: '#7c3aed',
  },
  C: {
    name: 'Conscientiousness',
    bar: 'bg-emerald-600',
    dot: 'bg-emerald-600',
    gradient: 'from-emerald-50 to-teal-50',
    caption: 'Self-discipline, organization, reliability.',
    low: 'Spontaneous, flexible, loose with structure.',
    high: 'Organized, dependable, self-disciplined.',
    hex: '#059669',
  },
  E: {
    name: 'Extraversion',
    bar: 'bg-orange-600',
    dot: 'bg-orange-600',
    gradient: 'from-amber-50 to-orange-50',
    caption: 'Sociability, assertiveness, positive energy.',
    low: 'Reserved, reflective, recharges in solitude.',
    high: 'Outgoing, energetic, draws energy from interaction.',
    hex: '#ea580c',
  },
  A: {
    name: 'Agreeableness',
    bar: 'bg-rose-600',
    dot: 'bg-rose-600',
    gradient: 'from-rose-50 to-pink-50',
    caption: 'Compassion, cooperation, trust in others.',
    low: 'Direct, competitive, willing to challenge.',
    high: 'Warm, cooperative, trusting and considerate.',
    hex: '#e11d48',
  },
  N: {
    name: 'Neuroticism',
    bar: 'bg-slate-700',
    dot: 'bg-slate-700',
    gradient: 'from-slate-50 to-blue-50',
    caption: 'Tendency to experience negative emotion under stress.',
    low: 'Even-keeled, resilient under pressure.',
    high: 'More sensitive to stress; feels emotion more intensely.',
    hex: '#334155',
  },
};

/**
 * Each trait is made up of 6 narrower behavioural tendencies — IPIP-NEO
 * calls these "facets" but we surface them to both employees and HR as
 * "aspects" to avoid jargon. Each has a `name` (the canonical IPIP-NEO
 * label) and a short `blurb`. Blurbs are intentionally:
 *   - Non-judgemental (no "good" or "bad" aspects)
 *   - Behavioural ("how this shows up at work / day-to-day")
 *   - One sentence each — meant to be glanceable next to the score
 */
export const ASPECT_META = {
  // Openness
  O1: { name: 'Imagination',
        blurb: 'How vivid your inner world is — daydreaming, fantasy, getting lost in thought.' },
  O2: { name: 'Artistic Interests',
        blurb: 'How drawn you are to beauty, art, design, and aesthetic experiences.' },
  O3: { name: 'Emotionality',
        blurb: 'How aware of and in tune with your own feelings you are.' },
  O4: { name: 'Adventurousness',
        blurb: 'How readily you embrace new experiences, places, and ways of doing things.' },
  O5: { name: 'Intellect',
        blurb: 'How much you enjoy abstract ideas, complex problems, and intellectual debate.' },
  O6: { name: 'Liberalism',
        blurb: 'How open you are to challenging traditional values and authority.' },

  // Conscientiousness
  C1: { name: 'Self-Efficacy',
        blurb: 'How confident you feel in your ability to get things done well.' },
  C2: { name: 'Orderliness',
        blurb: 'How much you value structure, tidiness, and a place for everything.' },
  C3: { name: 'Dutifulness',
        blurb: 'How strongly you feel a sense of moral obligation and responsibility.' },
  C4: { name: 'Achievement-Striving',
        blurb: 'How driven you are to set high standards and push yourself to meet them.' },
  C5: { name: 'Self-Discipline',
        blurb: 'How well you start and finish tasks despite distractions or boredom.' },
  C6: { name: 'Cautiousness',
        blurb: 'How carefully you think things through before you act.' },

  // Extraversion
  E1: { name: 'Friendliness',
        blurb: 'How easily you warm up to people and feel comfortable in their company.' },
  E2: { name: 'Gregariousness',
        blurb: 'How much you enjoy being in larger groups, parties, and crowds.' },
  E3: { name: 'Assertiveness',
        blurb: 'How readily you take charge, lead, and speak up in groups.' },
  E4: { name: 'Activity Level',
        blurb: 'How much energy and pace you prefer in your daily life.' },
  E5: { name: 'Excitement-Seeking',
        blurb: 'How much you crave stimulation, thrills, and bold experiences.' },
  E6: { name: 'Cheerfulness',
        blurb: 'How readily you experience and express positive emotion and joy.' },

  // Agreeableness
  A1: { name: 'Trust',
        blurb: 'How readily you assume that other people have good intentions.' },
  A2: { name: 'Morality',
        blurb: 'How much you value sincerity and avoid manipulation, even when it costs you.' },
  A3: { name: 'Altruism',
        blurb: 'How willing you are to help others, even at some cost to yourself.' },
  A4: { name: 'Cooperation',
        blurb: 'How readily you compromise and avoid conflict to keep things harmonious.' },
  A5: { name: 'Modesty',
        blurb: 'How comfortable you are downplaying your own achievements rather than promoting them.' },
  A6: { name: 'Sympathy',
        blurb: 'How easily you feel concern for others who are suffering or struggling.' },

  // Neuroticism / Emotional Sensitivity
  N1: { name: 'Anxiety',
        blurb: 'How often you feel worried, tense, or on-edge about what might go wrong.' },
  N2: { name: 'Anger',
        blurb: 'How quickly and intensely you feel frustrated or irritated when things go against you.' },
  N3: { name: 'Depression',
        blurb: 'How prone you are to feeling low, blue, or discouraged.' },
  N4: { name: 'Self-Consciousness',
        blurb: 'How sensitive you are to embarrassment or what others think of you.' },
  N5: { name: 'Immoderation',
        blurb: 'How strongly you feel cravings and urges (food, comfort, indulgences).' },
  N6: { name: 'Vulnerability',
        blurb: 'How you cope when you\u2019re under heavy pressure or in crisis.' },
};

/**
 * Map a percentile (0..100) into a 5-step qualitative bucket. Used in
 * place of raw / T-score numbers so a non-specialist reader can take
 * something away at a glance without translating jargon.
 *
 * `color` is an `<InfoBadge>` palette name (slate / blue / gray /
 * emerald / violet) so the chip is rendered through the canonical DS
 * component rather than hand-rolled Tailwind.
 */
export function qualitativeBucket(percentile) {
  if (percentile == null) return null;
  if (percentile < 15)  return { label: 'Low',           color: 'slate'   };
  if (percentile < 40)  return { label: 'Below average', color: 'blue'    };
  if (percentile < 60)  return { label: 'Average',       color: 'gray'    };
  if (percentile < 85)  return { label: 'Above average', color: 'emerald' };
  return                       { label: 'High',          color: 'violet'  };
}
